// Ad click-id & UTM capture for order source attribution.
// Captures fbclid (Meta), ttclid (TikTok) and UTM params from the landing URL,
// keeps them for 7 days, and derives the order's source_platform.
// Advertising/attribution only — never affects prices, orders totals, or external syncs.

const STORAGE_KEY = 'ad_click_attribution';
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export interface ClickAttribution {
  fbclid: string | null;
  ttclid: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  captured_at: number;
}

export type SourcePlatform = 'meta' | 'tiktok' | 'google' | 'other' | 'direct';

/** Derive the source platform from click ids / utm params. */
export function deriveSourcePlatform(a: {
  fbclid?: string | null;
  ttclid?: string | null;
  utm_source?: string | null;
}): SourcePlatform {
  if (a.fbclid) return 'meta';
  if (a.ttclid) return 'tiktok';
  const utm = (a.utm_source || '').toLowerCase();
  if (utm === 'facebook' || utm === 'fb' || utm === 'instagram' || utm === 'ig' || utm === 'meta') return 'meta';
  if (utm === 'tiktok' || utm === 'tt') return 'tiktok';
  if (utm === 'google' || utm === 'adwords' || utm === 'googleads') return 'google';
  if (utm) return 'other';
  return 'direct';
}

function readStored(): ClickAttribution | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ClickAttribution;
    if (!parsed.captured_at || Date.now() - parsed.captured_at > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Capture click ids / UTM params from the current URL.
 * A URL that carries any tracking param overwrites the stored attribution
 * (last-click wins, same as the ad platforms). URLs without params keep the
 * existing stored attribution untouched.
 */
export function captureClickIds(url: string = window.location.href): void {
  try {
    const params = new URL(url).searchParams;
    const fbclid = params.get('fbclid');
    const ttclid = params.get('ttclid');
    const utmSource = params.get('utm_source');

    if (!fbclid && !ttclid && !utmSource) return; // nothing new — keep existing

    const attribution: ClickAttribution = {
      fbclid: fbclid || null,
      ttclid: ttclid || null,
      utm_source: utmSource || null,
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      captured_at: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // ignore storage/parse errors
  }
}

/**
 * Get the stored attribution plus the derived source platform,
 * ready to spread into an order insert. Returns nulls when nothing was captured.
 */
export function getOrderAttribution(): {
  source_platform: SourcePlatform;
  fbclid: string | null;
  ttclid: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
} {
  const stored = readStored();
  if (!stored) {
    return {
      source_platform: 'direct',
      fbclid: null,
      ttclid: null,
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
    };
  }
  return {
    source_platform: deriveSourcePlatform(stored),
    fbclid: stored.fbclid,
    ttclid: stored.ttclid,
    utm_source: stored.utm_source,
    utm_medium: stored.utm_medium,
    utm_campaign: stored.utm_campaign,
  };
}
